// Session 52 · Nominatim address autocomplete for admin venue forms.
//
// - Debounces typing → hits OpenStreetMap Nominatim (free, no key).
// - Presents a compact dropdown of matching addresses.
// - On select, the caller receives the full parsed address so it can fill
//   address_line_1, city, county, state, postal_code, country, latitude,
//   longitude in one shot.
//
// Nominatim usage policy: max 1 req/sec, must set a descriptive User-Agent.
// Browsers can't set User-Agent, so we rely on the Referer + query-only calls
// and keep the debounce window generous (350ms) to stay well under the cap.
import { useEffect, useRef, useState } from "react";

const NOMINATIM_URL = "https://nominatim.openstreetmap.org/search";

async function fetchNominatim(query) {
  if (!query || query.trim().length < 3) return [];
  const url = `${NOMINATIM_URL}?format=json&addressdetails=1&limit=6&countrycodes=us&q=${encodeURIComponent(query)}`;
  const r = await fetch(url, {
    headers: { Accept: "application/json", "Accept-Language": "en" },
  });
  if (!r.ok) return [];
  const rows = await r.json();
  return Array.isArray(rows) ? rows : [];
}

function pickCity(addr) {
  return addr?.city || addr?.town || addr?.village || addr?.hamlet
    || addr?.municipality || addr?.suburb || "";
}

export default function NominatimAutocomplete({
  value, onChange, onPick, placeholder = "Start typing an address…", testid,
}) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const debounced = (q) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await fetchNominatim(q);
        setRows(results);
        setOpen(results.length > 0);
      } finally {
        setLoading(false);
      }
    }, 350);
  };

  const handleType = (v) => {
    onChange(v);
    if (v && v.trim().length >= 3) debounced(v);
    else { setRows([]); setOpen(false); }
  };

  const handlePick = (row) => {
    const addr = row.address || {};
    const streetParts = [addr.house_number, addr.road].filter(Boolean).join(" ").trim();
    const parsed = {
      display_name: row.display_name,
      address_line_1: streetParts || (row.display_name || "").split(",")[0],
      city: pickCity(addr),
      county: (addr.county || "").replace(/\s+County$/i, ""),
      state: addr.state || "",
      postal_code: addr.postcode || "",
      country: (addr.country_code || "us").toUpperCase(),
      latitude: row.lat ? parseFloat(row.lat) : null,
      longitude: row.lon ? parseFloat(row.lon) : null,
    };
    onPick(parsed);
    setOpen(false);
  };

  return (
    <div ref={boxRef} style={{ position: "relative" }} data-testid={testid}>
      <input
        value={value || ""}
        onChange={(e) => handleType(e.target.value)}
        onFocus={() => rows.length > 0 && setOpen(true)}
        placeholder={placeholder}
        style={{ width: "100%" }}
        data-testid={testid ? `${testid}-input` : undefined}
      />
      {loading && (
        <div style={{ position: "absolute", right: 10, top: 10, fontSize: 11, color: "rgba(255,255,255,0.4)" }}>…</div>
      )}
      {open && rows.length > 0 && (
        <ul
          data-testid={testid ? `${testid}-list` : undefined}
          style={{
            position: "absolute", zIndex: 30, left: 0, right: 0, top: "100%",
            marginTop: 4, listStyle: "none", padding: 0,
            background: "var(--navy-mid, #1a2340)",
            border: "1px solid rgba(212,168,75,0.22)", borderRadius: 8,
            maxHeight: 260, overflowY: "auto", boxShadow: "0 6px 22px rgba(0,0,0,0.35)",
          }}
        >
          {rows.map((row, i) => (
            <li key={row.place_id || i}>
              <button
                type="button"
                onClick={() => handlePick(row)}
                data-testid={testid ? `${testid}-option-${i}` : undefined}
                style={{
                  width: "100%", textAlign: "left", padding: "10px 12px",
                  background: "transparent", color: "#fff", border: "none",
                  borderBottom: i < rows.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none",
                  cursor: "pointer", fontSize: 13, lineHeight: 1.4,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(212,168,75,0.08)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                {row.display_name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
